import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { useAuth } from "hooks/useAuth";

const STORAGE_QUOTA_BYTES = 1 * 1024 * 1024 * 1024; // 1 GiB

function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const GraphOrganizationStorageUsage = ({
  _usedBytes,
  onViewDetailedUsage,
}) => {
  const navigate = useNavigate();
  const { chartmindStorageBytes, chartmindDocsLoading } = useAuth();

  const controlled = _usedBytes !== undefined;
  const usedBytes = controlled ? _usedBytes : chartmindStorageBytes;
  const loading = !controlled && chartmindDocsLoading;
  const error = "";

  const percent = Math.min(
    100,
    Math.max(0, ((Number(usedBytes) || 0) / STORAGE_QUOTA_BYTES) * 100),
  );
  const isCritical = percent > 90;
  const barColor = isCritical ? "error" : percent > 75 ? "warning" : "primary";

  const handleViewDetailedUsage = () => {
    if (typeof onViewDetailedUsage === "function") {
      onViewDetailedUsage();
      return;
    }
    navigate("/dashboard/files");
  };

  return (
    <Paper sx={{ mt: 3, p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", letterSpacing: "0.12em", fontWeight: 700 }}
          >
            Organization Storage
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total size of all documents uploaded by your organization.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={handleViewDetailedUsage}
          sx={{ flexShrink: 0, alignSelf: "flex-start" }}
        >
          View all files
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {loading ? (
          <Box
            sx={{
              flexGrow: 1,
              height: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : (
          <LinearProgress
            variant="determinate"
            value={percent}
            color={barColor}
            sx={{
              flexGrow: 1,
              height: 10,
              borderRadius: 5,
              "& .MuiLinearProgress-bar": { transition: "none" },
            }}
          />
        )}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 120, textAlign: "right" }}
        >
          {loading ? "—" : `${formatBytes(usedBytes)} / 1 GB`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default GraphOrganizationStorageUsage;
